import React, { useState, useEffect, useCallback, useRef } from 'react';
import { XCircle, X, CheckCircle, AlertTriangle, Calendar, Clock, Upload, Image as ImageIcon, FileText, File, Copy } from 'lucide-react';
import api from 'services/api';
import { LoadingSpinner } from 'components/common';
import NotificationToast from 'components/common/NotificationToast';
import { usePermissions } from 'hooks/usePermissions';

import ArquivoItem from './contasPagar/components/ArquivoItem';
import HeaderStats from './contasPagar/components/HeaderStats';
import FiltrosOtimizados from './contasPagar/components/FiltrosOtimizados';
import TabelaContasPagar from './contasPagar/components/TabelaContasPagar';
import GraficoContasPorDia from './contasPagar/components/GraficoContasPorDia';
import GraficoContasPorMes from './contasPagar/components/GraficoContasPorMes';
// import ResumoPorCategoria from './contasPagar/components/ResumoPorCategoria';
import AnaliseContaBancaria from './contasPagar/components/AnaliseContaBancaria';
import { formatarMoeda, detectarTipoDocumento, processarContas, contaCasaComBusca } from './contasPagar/utils';
import { processarDadosGraficoDia, processarDadosGraficoMes } from './contasPagar/utils/processadoresGraficos';
import { COMPANY_STORAGE_KEY } from 'types/company.js';
import { useReceivableTerm } from 'hooks/useReceivableTerm';
import ModalCriarContaPagar from './contasPagar/components/modal/ModalCriarContaPagar';
import ModalCriarLote from './contasPagar/components/modal/ModalCriarLote';
import ListaLotes from './contasPagar/components/ListaLotes';
import DetalhesLote from './contasPagar/components/DetalhesLote';

const REQUEST_DEDUPE_WINDOW_MS = 1200;
const requestDedupeCache = new Map();

const shouldBlockDuplicateRequest = (key) => {
  const now = Date.now();
  const lastRequestAt = requestDedupeCache.get(key);

  if (lastRequestAt && now - lastRequestAt < REQUEST_DEDUPE_WINDOW_MS) {
    return true;
  }

  requestDedupeCache.set(key, now);
  return false;
};

const useContasPagarData = () => {
  const [loading, setLoading] = useState(false);
  const [contasPagar, setContasPagar] = useState([]);
  const [contasProximos3Meses, setContasProximos3Meses] = useState([]);
  const [contasPagas, setContasPagas] = useState([]);
  const [contasProntasParaPagar, setContasProntasParaPagar] = useState([]);

  const [carregandoPagas, setCarregandoPagas] = useState(false);
  const [carregandoEmAberto, setCarregandoEmAberto] = useState(false);
  const [carregandoProntas, setCarregandoProntas] = useState(false);
  const [ultimaRequisicaoPagas, setUltimaRequisicaoPagas] = useState(null);
  const [ultimaRequisicaoEmAberto, setUltimaRequisicaoEmAberto] = useState(null);
  const [ultimaRequisicaoProntas, setUltimaRequisicaoProntas] = useState(null);

  const [filtroAtivo, setFiltroAtivo] = useState('todas');
  const [busca, setBusca] = useState('');

  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const pad = (n) => String(n).padStart(2, '0');
  const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const [range, setRange] = useState({ presetId: 'mes_atual', start: toISO(startOfMonth), end: toISO(endOfMonth) });

  const [notification, setNotification] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [filtroArquivosPagas, setFiltroArquivosPagas] = useState('todas');
  const [filtroArquivosTodas, setFiltroArquivosTodas] = useState('todas');
  const [filtroArquivosVencidas, setFiltroArquivosVencidas] = useState('todas');
  const [filtroArquivosProximas, setFiltroArquivosProximas] = useState('todas');
  const [filtroArquivosHoje, setFiltroArquivosHoje] = useState('todas');
  const [filtroArquivosProntasParaPagar, setFiltroArquivosProntasParaPagar] = useState('todas');
  const [filtroTipoDocumento, setFiltroTipoDocumento] = useState('todos');
  const [buscaDocumento, setBuscaDocumento] = useState('');
  const [negacaoFiltro, setNegacaoFiltro] = useState(false);
  const [filtroPlanejamento, setFiltroPlanejamento] = useState(null);
  const [filtroSubcategoria, setFiltroSubcategoria] = useState(null);
  const [filtroTipoPagamento, setFiltroTipoPagamento] = useState('todos');
  const [filtroContaBancaria, setFiltroContaBancaria] = useState(null);
  const [filtroUnidade, setFiltroUnidade] = useState(null);
  const preloadInicialExecutadoRef = useRef(false);
  const debounceRangeInicializadoRef = useRef(false);
  const carregarEmAbertoRef = useRef(() => {});
  const carregarPagasRef = useRef(() => {});
  const carregarProntasParaPagarRef = useRef(() => {});
  const carregarProximos3MesesRef = useRef(() => {});

  return {
    loading,
    setLoading,
    contasPagar,
    setContasPagar,
    contasProximos3Meses,
    setContasProximos3Meses,
    contasPagas,
    setContasPagas,
    contasProntasParaPagar,
    setContasProntasParaPagar,
    carregandoPagas,
    setCarregandoPagas,
    carregandoEmAberto,
    setCarregandoEmAberto,
    carregandoProntas,
    setCarregandoProntas,
    ultimaRequisicaoPagas,
    setUltimaRequisicaoPagas,
    ultimaRequisicaoEmAberto,
    setUltimaRequisicaoEmAberto,
    ultimaRequisicaoProntas,
    setUltimaRequisicaoProntas,
    filtroAtivo,
    setFiltroAtivo,
    busca,
    setBusca,
    range,
    setRange,
    notification,
    setNotification,
    refreshKey,
    setRefreshKey,
    filtroArquivosPagas,
    setFiltroArquivosPagas,
    filtroArquivosTodas,
    setFiltroArquivosTodas,
    filtroArquivosVencidas,
    setFiltroArquivosVencidas,
    filtroArquivosProximas,
    setFiltroArquivosProximas,
    filtroArquivosHoje,
    setFiltroArquivosHoje,
    filtroArquivosProntasParaPagar,
    setFiltroArquivosProntasParaPagar,
    filtroTipoDocumento,
    setFiltroTipoDocumento,
    buscaDocumento,
    setBuscaDocumento,
    negacaoFiltro,
    setNegacaoFiltro,
    filtroPlanejamento,
    setFiltroPlanejamento,
    filtroSubcategoria,
    setFiltroSubcategoria,
    filtroTipoPagamento,
    setFiltroTipoPagamento,
    filtroContaBancaria,
    setFiltroContaBancaria,
    filtroUnidade,
    setFiltroUnidade,
    preloadInicialExecutadoRef,
    debounceRangeInicializadoRef,
    carregarEmAbertoRef,
    carregarPagasRef,
    carregarProntasParaPagarRef,
    carregarProximos3MesesRef,
  };
};

const useContasPagarUI = () => {
  const [modalAberto, setModalAberto] = useState(false);
  const [contaSelecionada, setContaSelecionada] = useState(null);
  const [authorization, setAuthorization] = useState(null);
  const [arquivosNovos, setArquivosNovos] = useState([]);
  const [uploadingArquivos, setUploadingArquivos] = useState(false);
  const [modalCriarAberto, setModalCriarAberto] = useState(false);
  const [modoSelecao, setModoSelecao] = useState(false);
  const [contasSelecionadas, setContasSelecionadas] = useState([]);
  const [contasEmLotes, setContasEmLotes] = useState([]);
  const [modalCriarLoteAberto, setModalCriarLoteAberto] = useState(false);
  const [loteSelecionado, setLoteSelecionado] = useState(null);

  return {
    modalAberto,
    setModalAberto,
    contaSelecionada,
    setContaSelecionada,
    authorization,
    setAuthorization,
    arquivosNovos,
    setArquivosNovos,
    uploadingArquivos,
    setUploadingArquivos,
    modalCriarAberto,
    setModalCriarAberto,
    modoSelecao,
    setModoSelecao,
    contasSelecionadas,
    setContasSelecionadas,
    contasEmLotes,
    setContasEmLotes,
    modalCriarLoteAberto,
    setModalCriarLoteAberto,
    loteSelecionado,
    setLoteSelecionado,
  };
};

const ContasPagar = () => {
  const { hasPermission } = usePermissions();
  const canAuthorizePayment = hasPermission('administrativo.pagamento.authorize');
  const canCreateContaPagar = hasPermission('administrativo.contas_pagar.create');
  const canUploadContaPagarAttachment = hasPermission('administrativo.contas_pagar.attachments.upload');
  const term = useReceivableTerm();

  const {
    loading,
    setLoading,
    contasPagar,
    setContasPagar,
    contasProximos3Meses,
    setContasProximos3Meses,
    contasPagas,
    setContasPagas,
    contasProntasParaPagar,
    setContasProntasParaPagar,
    carregandoPagas,
    setCarregandoPagas,
    carregandoEmAberto,
    setCarregandoEmAberto,
    carregandoProntas,
    setCarregandoProntas,
    ultimaRequisicaoPagas,
    setUltimaRequisicaoPagas,
    ultimaRequisicaoEmAberto,
    setUltimaRequisicaoEmAberto,
    ultimaRequisicaoProntas,
    setUltimaRequisicaoProntas,
    filtroAtivo,
    setFiltroAtivo,
    busca,
    setBusca,
    range,
    setRange,
    notification,
    setNotification,
    refreshKey,
    setRefreshKey,
    filtroArquivosPagas,
    setFiltroArquivosPagas,
    filtroArquivosTodas,
    setFiltroArquivosTodas,
    filtroArquivosVencidas,
    setFiltroArquivosVencidas,
    filtroArquivosProximas,
    setFiltroArquivosProximas,
    filtroArquivosHoje,
    setFiltroArquivosHoje,
    filtroArquivosProntasParaPagar,
    setFiltroArquivosProntasParaPagar,
    filtroTipoDocumento,
    setFiltroTipoDocumento,
    buscaDocumento,
    setBuscaDocumento,
    negacaoFiltro,
    setNegacaoFiltro,
    filtroPlanejamento,
    setFiltroPlanejamento,
    filtroSubcategoria,
    setFiltroSubcategoria,
    filtroTipoPagamento,
    setFiltroTipoPagamento,
    filtroContaBancaria,
    setFiltroContaBancaria,
    filtroUnidade,
    setFiltroUnidade,
    preloadInicialExecutadoRef,
    debounceRangeInicializadoRef,
    carregarEmAbertoRef,
    carregarPagasRef,
    carregarProntasParaPagarRef,
    carregarProximos3MesesRef,
  } = useContasPagarData();

  const {
    modalAberto,
    setModalAberto,
    contaSelecionada,
    setContaSelecionada,
    authorization,
    setAuthorization,
    arquivosNovos,
    setArquivosNovos,
    uploadingArquivos,
    setUploadingArquivos,
    modalCriarAberto,
    setModalCriarAberto,
    modoSelecao,
    setModoSelecao,
    contasSelecionadas,
    setContasSelecionadas,
    contasEmLotes,
    setContasEmLotes,
    modalCriarLoteAberto,
    setModalCriarLoteAberto,
    loteSelecionado,
    setLoteSelecionado,
  } = useContasPagarUI();

  const detalhesModalRef = useRef(null);
  const detalhesModalCloseRef = useRef(null);
  const filtroInicializadoRef = useRef(false);

  const getSelectedCompanyName = () => {
    try {
      const ctx = localStorage.getItem(COMPANY_STORAGE_KEY);
      if (ctx) {
        const parsed = JSON.parse(ctx);
        if (parsed?.companies?.length > 0) {
          // Usa a primeira empresa do contexto atual
          return parsed.companies[0].name;
        }
      }
    } catch {}
    return 'MOV Fibra';
  };

  const abrirModalCriar = () => {
    if (!canCreateContaPagar) {
      showNotification('Você não tem permissão para criar contas a pagar', 'error');
      return;
    }

    setModalCriarAberto(true);
  };
  const fecharModalCriar = () => setModalCriarAberto(false);

  const normalizarContasEmLotes = useCallback(function normalizar(payload) {
    if (Array.isArray(payload)) {
      return payload
        .map((item) => (item && typeof item === 'object' ? item.id : item))
        .filter((id) => id !== undefined && id !== null);
    }

    if (payload && typeof payload === 'object') {
      return Object.values(payload)
        .map((item) => (item && typeof item === 'object' ? item.id : item))
        .filter((id) => id !== undefined && id !== null);
    }

    if (typeof payload === 'string') {
      const valor = payload.trim();
      if (!valor) return [];

      try {
        const parsed = JSON.parse(valor);
        return normalizar(parsed);
      } catch {
        return valor
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
      }
    }

    if (payload === undefined || payload === null) {
      return [];
    }

  return [payload];
  }, []);

  const handleToggleSelecao = (contaId) => {
    // Bloquear seleção de contas que já estão em lotes
    if (contasEmLotes.some((id) => String(id) === String(contaId))) {
      if (showNotification) {
        showNotification('Esta conta já está em um lote ativo e não pode ser selecionada', 'warning');
      }
      return;
    }
    
    setContasSelecionadas(prev => {
      if (prev.includes(contaId)) {
        return prev.filter(id => id !== contaId);
      } else {
        return [...prev, contaId];
      }
    });
  };

  const handleAtivarModoSelecao = async () => {
    setModoSelecao(true);
    setContasSelecionadas([]);
    
    // Buscar contas que já estão em lotes ativos
    try {
      const response = await api.get('/n8n/pagamento-lotes/contas-em-lotes');
      if (response.data.success) {
        setContasEmLotes(
          normalizarContasEmLotes(
            response.data.contas_bloqueadas ?? response.data.contas_em_lotes
          )
        );
      }
    } catch (error) {
      console.error('Erro ao buscar contas em lotes:', error);
      setContasEmLotes([]);
    }
  };

  const handleDesativarModoSelecao = () => {
    setModoSelecao(false);
    setContasSelecionadas([]);
    setContasEmLotes([]);
  };

  const handleAbrirModalCriarLote = () => {
    if (contasSelecionadas.length === 0) {
      showNotification('Selecione pelo menos uma conta para criar o lote', 'warning');
      return;
    }
    setModalCriarLoteAberto(true);
  };

  const handleLoteCriado = (lote) => {
    showNotification(`Lote #${lote.id} criado com sucesso! Primeira autorização realizada.`, 'success');
    setModalCriarLoteAberto(false);
    setModoSelecao(false);
    setContasSelecionadas([]);
    // Se não estiver na aba prontas_para_pagar, mudar para ela
    if (filtroAtivo !== 'prontas_para_pagar') {
      setFiltroAtivo('prontas_para_pagar');
    }
    setRefreshKey(prev => prev + 1); // Forçar refresh da lista de lotes
  };

  // Desativar modo seleção quando sair da aba "Prontas para Pagar"
  useEffect(() => {
    if (filtroAtivo !== 'prontas_para_pagar' && modoSelecao) {
      setModoSelecao(false);
      setContasSelecionadas([]);
    }
  }, [filtroAtivo, modoSelecao, setModoSelecao, setContasSelecionadas]);

  // Atualizar lista de contas bloqueadas quando o modo de seleção estiver ativo ou quando houver refresh
  useEffect(() => {
    if (modoSelecao && filtroAtivo === 'prontas_para_pagar') {
      // Buscar contas que já estão em lotes ativos ou autorizadas individualmente
      const buscarContasBloqueadas = async () => {
        try {
          const response = await api.get('/n8n/pagamento-lotes/contas-em-lotes');
          if (response.data.success) {
            setContasEmLotes(
              normalizarContasEmLotes(
                response.data.contas_bloqueadas ?? response.data.contas_em_lotes
              )
            );
          }
        } catch (error) {
          console.error('Erro ao buscar contas bloqueadas:', error);
          setContasEmLotes([]);
        }
      };
      buscarContasBloqueadas();
    }
  }, [modoSelecao, filtroAtivo, refreshKey, normalizarContasEmLotes, setContasEmLotes]);

  const handleLoteClick = (loteId) => {
    setLoteSelecionado(loteId);
  };

  const handleFecharDetalhesLote = () => {
    setLoteSelecionado(null);
  };

  // Helper para normalizar strings nas comparações
  const normalizeText = (s) => (s ?? '').toString().normalize('NFKC').trim().toLowerCase();

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const copiarTexto = async (valor, label = 'Texto') => {
    if (!valor) {
      showNotification(`${label} indisponível para cópia`, 'warning');
      return;
    }
    try {
      await navigator.clipboard.writeText(String(valor));
      showNotification(`${label} copiado com sucesso`);
    } catch {
      showNotification(`Não foi possível copiar ${label.toLowerCase()}`, 'error');
    }
  };

  const getContaStatusInfo = (status, dias) => {
    switch (status) {
      case 'paga':
        return { icon: <CheckCircle className="h-4 w-4" />, label: 'Paga', bgColor: 'bg-[color:var(--status-good-bg,var(--surface-muted))]', color: 'text-[color:var(--status-good-fg,var(--text-primary))]' };
      case 'paga_parcialmente':
        return { icon: <CheckCircle className="h-4 w-4" />, label: 'Paga Parcialmente', bgColor: 'bg-[color:var(--status-info-bg,var(--surface-muted))]', color: 'text-[color:var(--status-info-fg,var(--text-primary))]' };
      case 'cancelada':
        return { icon: <XCircle className="h-4 w-4" />, label: 'Cancelada', bgColor: 'bg-[color:var(--surface-muted)]', color: 'text-[color:var(--text-primary)]' };
      case 'A':
        if (dias < 0) return { icon: <AlertTriangle className="h-4 w-4" />, label: 'Vencida', bgColor: 'bg-[color:var(--status-danger-bg,var(--surface-muted))]', color: 'text-[color:var(--status-danger-fg,var(--text-primary))]' };
        if (dias === 0) return { icon: <Calendar className="h-4 w-4" />, label: 'Vence hoje', bgColor: 'bg-[color:var(--status-warning-bg,var(--surface-muted))]', color: 'text-[color:var(--status-warning-fg,var(--text-primary))]' };
        if (dias <= 7) return { icon: <Clock className="h-4 w-4" />, label: 'Próxima', bgColor: 'bg-[color:var(--status-warning-bg,var(--surface-muted))]', color: 'text-[color:var(--status-warning-fg,var(--text-primary))]' };
        return { icon: <Clock className="h-4 w-4" />, label: 'Em aberto', bgColor: 'bg-[color:var(--status-info-bg,var(--surface-muted))]', color: 'text-[color:var(--status-info-fg,var(--text-primary))]' };
      default:
        return { icon: <Clock className="h-4 w-4" />, label: 'Em aberto', bgColor: 'bg-[color:var(--status-info-bg,var(--surface-muted))]', color: 'text-[color:var(--status-info-fg,var(--text-primary))]' };
    }
  };

  const abrirModal = (conta) => {
    setContaSelecionada(conta);
    setModalAberto(true);
  };
  const fecharModal = () => {
    setModalAberto(false);
    setContaSelecionada(null);
    setAuthorization(null);
    setArquivosNovos([]);
  };

  const handleDetalhesModalKeyDown = (event) => {
    if (!modalAberto) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      fecharModal();
      return;
    }

    if (event.key !== 'Tab') return;
    const modalNode = detalhesModalRef.current;
    if (!modalNode) return;

    const focusables = Array.from(
      modalNode.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    ).filter((element) => !element.hasAttribute('disabled'));

    if (focusables.length === 0) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  useEffect(() => {
    if (!modalAberto) return undefined;

    const previousActiveElement = document.activeElement;
    const frame = requestAnimationFrame(() => {
      detalhesModalCloseRef.current?.focus();
    });

    return () => {
      cancelAnimationFrame(frame);
      if (previousActiveElement && typeof previousActiveElement.focus === 'function') {
        previousActiveElement.focus();
      }
    };
  }, [modalAberto]);

  // Função para atualizar a conta selecionada quando o modal está aberto
  const atualizarContaSelecionada = useCallback(() => {
    if (!contaSelecionada?.id || !modalAberto) return;
    
    // Buscar a conta atualizada nas listas carregadas
    let contasAtualizadas = contasPagar;
    if (filtroAtivo === 'pagas') {
      contasAtualizadas = contasPagas;
    } else if (filtroAtivo === 'prontas_para_pagar') {
      contasAtualizadas = contasProntasParaPagar;
    }
    const contaAtualizada = contasAtualizadas.find(c => c.id === contaSelecionada.id);
    
    if (contaAtualizada) {
      // Comparar se houve mudanças significativas (arquivos, status, valores)
      const arquivosAntigos = JSON.stringify(contaSelecionada.arquivos || []);
      const arquivosNovos = JSON.stringify(contaAtualizada.arquivos || []);
      const arquivosDiferentes = arquivosAntigos !== arquivosNovos;
      const statusDiferente = contaAtualizada.status !== contaSelecionada.status;
      const valorDiferente = parseFloat(contaAtualizada.valor_pago || 0) !== parseFloat(contaSelecionada.valor_pago || 0);
      
      if (arquivosDiferentes || statusDiferente || valorDiferente) {
        setContaSelecionada(contaAtualizada);
        
        // Feedback visual sutil quando há atualização
        if (arquivosDiferentes) {
          console.debug('Modal atualizado: novos arquivos detectados');
        }
        if (statusDiferente) {
          console.debug('Modal atualizado: status alterado');
        }
        if (valorDiferente) {
          console.debug('Modal atualizado: valor pago alterado');
        }
      }
    }
  }, [contaSelecionada, modalAberto, filtroAtivo, contasPagas, contasPagar, contasProntasParaPagar, setContaSelecionada]);

  // Função para buscar autorização
  const buscarAutorizacao = useCallback(async () => {
    if (!contaSelecionada?.id) return;
    
    try {
      const response = await api.get('/n8n/administracao/a_pagar/authorization-status', {
        params: { conta_pagar_id: contaSelecionada.id }
      });
      
      if (response.data.success && response.data.authorization) {
        setAuthorization(response.data.authorization);
      } else {
        setAuthorization(null);
      }
    } catch (error) {
      console.error('Erro ao buscar autorização:', error);
      setAuthorization(null);
    }
  }, [contaSelecionada?.id, setAuthorization]);

  // Atualização ao abrir o modal (sem polling automático)
  useEffect(() => {
    if (!modalAberto || !contaSelecionada?.id) return;

    let isMounted = true;

    // Atualizar imediatamente ao abrir o modal (após um pequeno delay para garantir que o estado está pronto)
    const timeoutId = setTimeout(() => {
      if (isMounted && modalAberto && contaSelecionada?.id) {
        atualizarContaSelecionada();
        buscarAutorizacao();
      }
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [modalAberto, contaSelecionada?.id, atualizarContaSelecionada, buscarAutorizacao]);

  // Função para comprimir imagens (reutilizada do ModalCriarContaPagar)
  const compressImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          const maxWidth = 1920;
          const maxHeight = 1920;
          const quality = 0.8;

          if (width > maxWidth || height > maxHeight) {
            if (width > height) {
              if (width > maxWidth) {
                height = (height * maxWidth) / width;
                width = maxWidth;
              }
            } else {
              if (height > maxHeight) {
                width = (width * maxHeight) / height;
                height = maxHeight;
              }
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob(
            (blob) => resolve(blob || file),
            file.type,
            quality
          );
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // Função para processar arquivos selecionados
  const handleAdicionarArquivos = async (e) => {
    if (!canUploadContaPagarAttachment) {
      showNotification('Você não tem permissão para anexar arquivos em contas a pagar', 'error');
      e.target.value = '';
      return;
    }

    const files = Array.from(e.target.files || []);
    const allowedMime = new Set(['application/pdf','image/png','image/jpeg','image/jpg','image/gif','image/webp']);
    const MAX_FILE_SIZE = 8 * 1024 * 1024; // 8MB
    const novos = [];
    
    for (const f of files) {
      if (!allowedMime.has(f.type)) {
        showNotification(`Formato não suportado: ${f.name}`, 'error');
        continue;
      }
      
      if (f.size > MAX_FILE_SIZE) {
        const tamanhoMB = (f.size / 1024 / 1024).toFixed(2);
        showNotification(`Arquivo ${f.name} muito grande (${tamanhoMB} MB). Máximo: 8 MB`, 'error');
        continue;
      }
      
      try {
        let fileToProcess = f;
        
        // Comprimir imagens (não PDFs)
        if (f.type.startsWith('image/') && f.type !== 'image/gif') {
          fileToProcess = await compressImage(f);
        }
        
        // Converter para base64
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(fileToProcess);
        });
        
        novos.push({ 
          file: f, 
          descricao: f.name, 
          base64, 
          ready: true 
        });
      } catch (error) {
        showNotification(`Erro ao processar ${f.name}`, 'error');
      }
    }
    
    setArquivosNovos(prev => [...prev, ...novos]);
    e.target.value = '';
  };

  const removerArquivoNovo = (idx) => {
    setArquivosNovos(prev => prev.filter((_, i) => i !== idx));
  };

  // Função para enviar arquivos
  const enviarArquivos = async () => {
    if (arquivosNovos.length === 0 || !contaSelecionada?.id) return;

    if (!canUploadContaPagarAttachment) {
      showNotification('Você não tem permissão para anexar arquivos em contas a pagar', 'error');
      return;
    }
    
    setUploadingArquivos(true);
    const empresa = getSelectedCompanyName();
    let sucesso = 0;
    let erros = [];
    
    for (const arquivo of arquivosNovos) {
      try {
        await api.post('/n8n/administracao/a_pagar/novo_arquivo', {
          id_conta: contaSelecionada.id,
          descricao: arquivo.descricao,
          arquivo_base64: arquivo.base64,
          empresa,
        });
        sucesso++;
      } catch (error) {
        erros.push({ 
          nome: arquivo.descricao, 
          erro: error.response?.data?.message || error.message || 'Erro desconhecido' 
        });
      }
    }
    
    setUploadingArquivos(false);
    
    if (erros.length > 0) {
      showNotification(
        `Arquivos enviados: ${sucesso}. Erros: ${erros.length}`,
        'error'
      );
    } else {
      showNotification(`${sucesso} arquivo(s) adicionado(s) com sucesso!`);
      // Limpar arquivos novos
      setArquivosNovos([]);
          // Aguardar um pouco para garantir que o N8N processou
          await new Promise(resolve => setTimeout(resolve, 500));
          // Recarregar dados FORÇANDO bypass do cache
          if (filtroAtivo === 'pagas') {
            await carregarPagas(true);
          } else if (filtroAtivo === 'prontas_para_pagar') {
            await carregarProntasParaPagar(true);
          } else {
            await carregarEmAberto(true);
          }
      // Atualizar conta selecionada com os novos dados (usando a função centralizada)
      // Aguardar um pouco mais para garantir que o estado foi atualizado
      setTimeout(() => {
        atualizarContaSelecionada();
      }, 200);
    }
  };

  const getFiltroArquivosAtual = () => {
    switch (filtroAtivo) {
      case 'todas': return filtroArquivosTodas;
      case 'vencidas': return filtroArquivosVencidas;
      case 'proxima_vencimento': return filtroArquivosProximas;
      case 'hoje': return filtroArquivosHoje;
      case 'pagas': return filtroArquivosPagas;
      case 'prontas_para_pagar': return filtroArquivosProntasParaPagar;
      default: return 'todas';
    }
  };
  const setFiltroArquivosAtual = (valor) => {
    switch (filtroAtivo) {
      case 'todas': setFiltroArquivosTodas(valor); break;
      case 'vencidas': setFiltroArquivosVencidas(valor); break;
      case 'proxima_vencimento': setFiltroArquivosProximas(valor); break;
      case 'hoje': setFiltroArquivosHoje(valor); break;
      case 'pagas': setFiltroArquivosPagas(valor); break;
      case 'prontas_para_pagar': setFiltroArquivosProntasParaPagar(valor); break;
      default: setFiltroArquivosTodas(valor); break;
    }
  };

  const temArquivos = (conta) => {
    if (!conta.arquivos) return false;
    if (Array.isArray(conta.arquivos)) {
      return conta.arquivos.some(arq => arq && arq.arquivo && arq.id);
    }
    try {
      const arquivos = JSON.parse(conta.arquivos);
      return Array.isArray(arquivos) && arquivos.some(arq => arq && arq.arquivo && arq.id);
    } catch {
      return false;
    }
  };
  const contarArquivos = (conta) => {
    if (!conta.arquivos) return 0;
    if (Array.isArray(conta.arquivos)) {
      return conta.arquivos.filter(arq => arq && arq.arquivo && arq.id).length;
    }
    try {
      const arquivos = JSON.parse(conta.arquivos);
      return Array.isArray(arquivos) ? arquivos.filter(arq => arq && arq.arquivo && arq.id).length : 0;
    } catch {
      return 0;
    }
  };

  const normalizarDatasetN8n = (dataset) => {
    if (!dataset) return [];
    if (!Array.isArray(dataset)) return [dataset];
    // Alguns fluxos do N8N (ex: MOV Fit) retornam `data` como array dentro de array
    return dataset.flatMap((item) => (Array.isArray(item) ? item : [item]));
  };

  const extrairContasAPagar = (value, depth = 0) => {
    if (!value || depth > 3) return [];

    if (Array.isArray(value)) {
      return value.flatMap((v) => extrairContasAPagar(v, depth + 1));
    }

    if (typeof value === 'object') {
      const isContaDireta =
        (Object.prototype.hasOwnProperty.call(value, 'id') ||
          Object.prototype.hasOwnProperty.call(value, 'codigo')) &&
        (Object.prototype.hasOwnProperty.call(value, 'valor') ||
          Object.prototype.hasOwnProperty.call(value, 'data_vencimento') ||
          Object.prototype.hasOwnProperty.call(value, 'fornecedor'));

      if (isContaDireta) return [value];

      const chavesContas = ['contas a pagar', 'contas_a_pagar', 'contasApagar'];
      const listaDireta = chavesContas
        .map((chave) => value[chave])
        .find((lista) => Array.isArray(lista));

      if (Array.isArray(listaDireta)) {
        const looksLikeConta = listaDireta.some(
          (item) =>
            item &&
            typeof item === 'object' &&
            (Object.prototype.hasOwnProperty.call(item, 'id') ||
              Object.prototype.hasOwnProperty.call(item, 'codigo') ||
              Object.prototype.hasOwnProperty.call(item, 'descricao') ||
              Object.prototype.hasOwnProperty.call(item, 'status'))
        );

        if (looksLikeConta) return listaDireta;

        // Alguns fluxos retornam wrappers contendo outra lista de contas
        return listaDireta.flatMap((v) => extrairContasAPagar(v, depth + 1));
      }
    }

    return [];
  };

  const normalizarRespostaN8n = (payload) => {
    return payload ?? null;
  };

  const extrairDatasetN8n = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload)) {
      // Formato: [{ success, data: [...] }, ...]
      if (payload.some((item) => item && typeof item === 'object' && Array.isArray(item.data))) {
        return payload.flatMap((item) => (Array.isArray(item?.data) ? item.data : []));
      }
      // Formato: [{ contas_a_pagar: [...] }] ou lista direta de contas
      return payload;
    }
    // Formato padrão: { success, data: [...] }
    if (Array.isArray(payload.data)) return payload.data;
    // Formato alternativo: { contas_a_pagar: [...] } / { "contas a pagar": [...] }
    return payload;
  };

  const carregarEmAberto = async (forceRefresh = false) => {
    const empresa = getSelectedCompanyName();
    const filtroArquivosAtual = getFiltroArquivosAtual();
    const requestKey = [
      'a_pagar',
      'em_aberto',
      empresa,
      range.start,
      range.end,
      filtroAtivo,
      busca || '',
      filtroArquivosAtual,
      filtroTipoDocumento,
      buscaDocumento || '',
      negacaoFiltro ? '1' : '0',
      filtroPlanejamento || '',
      filtroSubcategoria || '',
      filtroTipoPagamento,
      filtroContaBancaria || '',
      filtroUnidade || '',
    ].join('|');

    if (shouldBlockDuplicateRequest(requestKey)) {
      console.debug('Requisição duplicada de contas em aberto ignorada...');
      return;
    }

    // Evitar requisições simultâneas
    if (carregandoEmAberto) {
      console.debug('Requisição de contas em aberto já em andamento, ignorando...');
      return;
    }

    // Evitar requisições muito frequentes (mínimo 2 segundos entre requisições)
    const agora = Date.now();
    if (ultimaRequisicaoEmAberto && (agora - ultimaRequisicaoEmAberto) < 2000 && !forceRefresh) {
      console.debug('Requisição muito frequente, ignorando...');
      return;
    }

    setCarregandoEmAberto(true);
    setLoading(true);
    setUltimaRequisicaoEmAberto(agora);

    try {
      const params = {
        action: 'em_aberto',
        data_inicio: range.start,
        data_fim: range.end
      };
      
      // Adicionar timestamp para forçar bypass do cache se necessário
      if (forceRefresh) {
        params._t = Date.now();
      }
      
      const response = await api.post('/n8n/administracao/a_pagar', params, { timeout: 130000 });
      
      const payload = normalizarRespostaN8n(response.data);
      // Validar resposta
      if (payload && payload.success !== false) {
        const dadosBrutos = normalizarDatasetN8n(extrairDatasetN8n(payload));
        const contas = extrairContasAPagar(dadosBrutos);
        const processadas = processarContas(contas);
        const compat = processadas.map(c => ({ ...c, data_vencimento_convertida: c.dataVencimento || c.data_vencimento_convertida }));
        setContasPagar(compat);
        showNotification(`${compat.length} contas carregadas com sucesso`);
      } else {
        // Se success: false, não limpar dados (manter cache)
        console.warn('API retornou success: false para contas em aberto');
        showNotification('Erro ao carregar contas a pagar', 'error');
      }
    } catch (e) {
      console.error('Erro ao carregar contas a pagar:', e);
      // Não limpar dados em caso de erro (manter cache)
      showNotification('Erro ao carregar contas a pagar', 'error');
    } finally {
      setCarregandoEmAberto(false);
      setLoading(false);
    }
  };

  const carregarPagas = async (forceRefresh = false) => {
    const empresa = getSelectedCompanyName();
    const requestKey = [
      'a_pagar',
      'pagas',
      empresa,
      range.start,
      range.end,
      filtroArquivosPagas,
      filtroTipoDocumento,
      buscaDocumento || '',
      negacaoFiltro ? '1' : '0',
      filtroPlanejamento || '',
      filtroSubcategoria || '',
      filtroTipoPagamento,
      filtroContaBancaria || '',
      filtroUnidade || '',
    ].join('|');

    if (shouldBlockDuplicateRequest(requestKey)) {
      console.debug('Requisição duplicada de contas pagas ignorada...');
      return;
    }

    // Evitar requisições simultâneas
    if (carregandoPagas) {
      console.debug('Requisição de contas pagas já em andamento, ignorando...');
      return;
    }

    // Evitar requisições muito frequentes (mínimo 2 segundos entre requisições)
    const agora = Date.now();
    if (ultimaRequisicaoPagas && (agora - ultimaRequisicaoPagas) < 2000 && !forceRefresh) {
      console.debug('Requisição muito frequente, ignorando...');
      return;
    }

    setCarregandoPagas(true);
    setLoading(true);
    setUltimaRequisicaoPagas(agora);

    try {
      const params = {
        action: 'pagas',
        data_inicio: range.start,
        data_fim: range.end
      };
      
      // Adicionar timestamp para forçar bypass do cache se necessário
      if (forceRefresh) {
        params._t = Date.now();
      }
      
      const response = await api.post('/n8n/administracao/a_pagar', params, { timeout: 130000 });
      
      const payload = normalizarRespostaN8n(response.data);
      // Validar resposta
      if (payload && payload.success !== false) {
        const dadosBrutos = normalizarDatasetN8n(extrairDatasetN8n(payload));
        const contas = extrairContasAPagar(dadosBrutos);
        const processadas = processarContas(contas);
        const compat = processadas.map(c => ({ ...c, data_vencimento_convertida: c.dataVencimento || c.data_vencimento_convertida }));
        setContasPagas(compat);
      } else {
        // Se success: false, não limpar dados (manter cache)
        console.warn('API retornou success: false para contas pagas');
      }
    } catch (e) {
      console.error('Erro ao carregar contas pagas:', e);
      // Não limpar dados em caso de erro (manter cache)
    } finally {
      setCarregandoPagas(false);
      setLoading(false);
    }
  };

  const carregarProntasParaPagar = async (forceRefresh = false) => {
    const empresa = getSelectedCompanyName();
    const requestKey = [
      'a_pagar',
      'prontas_para_pagar',
      empresa,
      range.start,
      range.end,
      filtroArquivosProntasParaPagar,
      filtroTipoDocumento,
      buscaDocumento || '',
      negacaoFiltro ? '1' : '0',
      filtroPlanejamento || '',
      filtroSubcategoria || '',
      filtroTipoPagamento,
      filtroContaBancaria || '',
      filtroUnidade || '',
    ].join('|');

    if (shouldBlockDuplicateRequest(requestKey)) {
      console.debug('Requisição duplicada de contas prontas para pagar ignorada...');
      return;
    }

    // Evitar requisições simultâneas
    if (carregandoProntas) {
      console.debug('Requisição de contas prontas para pagar já em andamento, ignorando...');
      return;
    }

    // Evitar requisições muito frequentes (mínimo 2 segundos entre requisições)
    const agora = Date.now();
    if (ultimaRequisicaoProntas && (agora - ultimaRequisicaoProntas) < 2000 && !forceRefresh) {
      console.debug('Requisição muito frequente, ignorando...');
      return;
    }

    setCarregandoProntas(true);
    setLoading(true);
    setUltimaRequisicaoProntas(agora);

    try {
      const params = {
        action: 'prontas_para_pagar',
        data_inicio: range.start,
        data_fim: range.end
      };
      
      // Adicionar timestamp para forçar bypass do cache se necessário
      if (forceRefresh) {
        params._t = Date.now();
      }
      
      const response = await api.post('/n8n/administracao/a_pagar', params, { timeout: 130000 });
      
      const payload = normalizarRespostaN8n(response.data);
      // Validar resposta
      if (payload && payload.success !== false) {
        const dadosBrutos = normalizarDatasetN8n(extrairDatasetN8n(payload));
        const contas = extrairContasAPagar(dadosBrutos).filter(
          (c) => c && typeof c === 'object' && Object.keys(c).length > 0
        );
        const processadas = processarContas(contas);
        const compat = processadas.map(c => ({ ...c, data_vencimento_convertida: c.dataVencimento || c.data_vencimento_convertida }));
        setContasProntasParaPagar(compat);
        // Não mostrar notificação quando está vazio - é um caso válido
        if (compat.length > 0) {
          showNotification(`${compat.length} contas prontas para pagar carregadas com sucesso`);
        }
      } else {
        // Se success: false, não limpar dados (manter cache)
        console.warn('API retornou success: false para contas prontas para pagar');
        showNotification('Erro ao carregar contas prontas para pagar', 'error');
      }
    } catch (e) {
      console.error('Erro ao carregar contas prontas para pagar:', e);
      // Não limpar dados em caso de erro (manter cache)
      showNotification('Erro ao carregar contas prontas para pagar', 'error');
    } finally {
      setCarregandoProntas(false);
      setLoading(false);
    }
  };

  // Carregar dataset específico para "próximos 3 meses" (lógica do backup)
  const carregarProximos3Meses = async () => {
    const empresa = getSelectedCompanyName();
    const requestKey = ['a_pagar', 'proximos_3_meses', empresa].join('|');

    if (shouldBlockDuplicateRequest(requestKey)) {
      console.debug('Requisição duplicada de próximos 3 meses ignorada...');
      return;
    }

    try {
      const response = await api.post('/n8n/administracao/a_pagar', {
        action: 'proximos_3_meses'
      }, { timeout: 130000 });
      const payload = normalizarRespostaN8n(response.data);
      if (payload?.success) {
        const dadosBrutos = normalizarDatasetN8n(extrairDatasetN8n(payload));
        const contas = extrairContasAPagar(dadosBrutos);
        const proc = processarContas(contas).map(c => ({ ...c, data_vencimento_convertida: c.dataVencimento || c.data_vencimento_convertida }));
        setContasProximos3Meses(proc);
      } else {
        setContasProximos3Meses([]);
      }
    } catch (e) {
      console.error('Erro ao carregar próximos 3 meses:', e);
      setContasProximos3Meses([]);
    }
  };

  carregarEmAbertoRef.current = carregarEmAberto;
  carregarPagasRef.current = carregarPagas;
  carregarProntasParaPagarRef.current = carregarProntasParaPagar;
  carregarProximos3MesesRef.current = carregarProximos3Meses;

  // Hook customizado de debounce para evitar múltiplas requisições
  const useDebounce = (callback, delay, deps) => {
    const timeoutRef = useRef(null);
    const callbackRef = useRef(callback);
    const depsKey = deps.join('|');

    useEffect(() => {
      callbackRef.current = callback;
    }, [callback]);
    
    useEffect(() => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      timeoutRef.current = setTimeout(() => {
        callbackRef.current();
      }, delay);
      
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, [delay, depsKey]);
  };

  useEffect(() => {
    if (preloadInicialExecutadoRef.current) return;
    preloadInicialExecutadoRef.current = true;

    carregarEmAbertoRef.current();
    carregarPagasRef.current();
    carregarProntasParaPagarRef.current();
    carregarProximos3MesesRef.current();
    // Carregamento inicial das abas para manter os contadores disponíveis
    // sem depender de clique em cada filtro.
  }, [
    preloadInicialExecutadoRef,
    debounceRangeInicializadoRef,
    carregarEmAbertoRef,
    carregarPagasRef,
    carregarProntasParaPagarRef,
    carregarProximos3MesesRef
  ]);

  // Recarregar todos os datasets quando o período mudar, com debounce
  useDebounce(() => {
    if (!debounceRangeInicializadoRef.current) {
      debounceRangeInicializadoRef.current = true;
      return;
    }

    if (!carregandoEmAberto && !carregandoPagas && !carregandoProntas) {
      carregarEmAbertoRef.current();
      carregarPagasRef.current();
      carregarProntasParaPagarRef.current();
      carregarProximos3MesesRef.current();
    }
  }, 500, [range.start, range.end]); // 500ms de debounce

  // Recarregar dados quando a empresa mudar
  useEffect(() => {
    const handleCompanyChange = () => {
      // Recarregar todos os datasets quando a empresa muda
      carregarEmAberto(true);
      carregarPagas(true);
      carregarProntasParaPagar(true);
      carregarProximos3Meses();

      // Forçar atualização de componentes dependentes da empresa (ex.: ListaLotes)
      setRefreshKey(prev => prev + 1);

      // Evitar estado stale ao trocar de empresa
      setLoteSelecionado(null);
      setModoSelecao(false);
      setContasSelecionadas([]);
      setContasEmLotes([]);
    };

    window.addEventListener('company-context-changed', handleCompanyChange);
    window.addEventListener('company-changed', handleCompanyChange);

    return () => {
      window.removeEventListener('company-context-changed', handleCompanyChange);
      window.removeEventListener('company-changed', handleCompanyChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recarregar apenas o dataset ativo quando o filtro muda
  // Filtros locais (vencidas, hoje, proxima_vencimento) não precisam recarregar dados
  useEffect(() => {
    // Filtros que requerem dados do servidor
    const filtrosComDados = ['pagas', 'prontas_para_pagar', 'todas'];
    
    // Só recarregar se for um filtro que requer dados do servidor
    if (!filtroInicializadoRef.current) {
      filtroInicializadoRef.current = true;
      return;
    }

    if (filtrosComDados.includes(filtroAtivo)) {
      if (filtroAtivo === 'pagas') {
        carregarPagas();
      } else if (filtroAtivo === 'prontas_para_pagar') {
        carregarProntasParaPagar();
      } else {
        carregarEmAberto();
      }
    }
    // Filtros locais ('vencidas', 'hoje', 'proxima_vencimento') apenas filtram os dados já carregados
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroAtivo]);

  const getVencimento = (conta) => {
    const base = conta.data_vencimento_convertida || conta.dataVencimento;
    return base ? new Date(base) : null;
  };

  // Função para processar data de pagamento (formato brasileiro: dd/mm/yyyy hh:mm:ss)
  const getDataPagamento = (conta) => {
    if (!conta.data_pagamento) return null;
    
    try {
      const dataStr = String(conta.data_pagamento).trim();
      
      // Verificar se está no formato brasileiro "dd/mm/yyyy" ou "dd/mm/yyyy hh:mm:ss"
      const matchBrasileiro = dataStr.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}):(\d{2}))?/);
      if (matchBrasileiro) {
        const [, dia, mes, ano, hora = '00', minuto = '00', segundo = '00'] = matchBrasileiro;
        const dateObj = new Date(`${ano}-${mes}-${dia}T${hora}:${minuto}:${segundo}`);
        if (!isNaN(dateObj.getTime())) {
          return dateObj;
        }
      }
      
      // Se não é formato brasileiro, tentar parsear como ISO ou formato padrão
      const dateISO = new Date(dataStr);
      if (!isNaN(dateISO.getTime())) {
        return dateISO;
      }
      
      return null;
    } catch {
      return null;
    }
  };

	  let contasFonte = contasPagar;
	  if (filtroAtivo === 'pagas') {
	    contasFonte = contasPagas;
    // Ordenar por data de pagamento (mais antiga primeiro) quando for subaba de pagas
    contasFonte = [...contasFonte].sort((a, b) => {
      const dataPagA = getDataPagamento(a);
      const dataPagB = getDataPagamento(b);
      if (dataPagA && dataPagB) {
        return dataPagA - dataPagB; // Crescente (mais antiga primeiro)
      }
      if (dataPagA) return -1;
      if (dataPagB) return 1;
      // Se não tem data de pagamento, manter ordem original
      return 0;
    });
	  } else if (filtroAtivo === 'prontas_para_pagar') {
	    contasFonte = contasProntasParaPagar;
	  }
	  const hasUnidade = contasFonte.some(c => !!c?.unidade);
	  let contasFiltradas = contasFonte;
  if (filtroAtivo === 'vencidas') {
    contasFiltradas = contasFonte.filter(c => {
      const v = getVencimento(c);
      if (!v) return false;
      const hoje = new Date();
      hoje.setHours(0,0,0,0);
      return v < hoje;
    });
  } else if (filtroAtivo === 'proxima_vencimento') {
    contasFiltradas = contasFonte.filter(c => {
      const v = getVencimento(c);
      if (!v) return false;
      const hoje = new Date();
      const proximos7 = new Date();
      proximos7.setDate(hoje.getDate() + 7);
      hoje.setHours(0,0,0,0);
      proximos7.setHours(23,59,59,999);
      return v >= hoje && v <= proximos7;
    });
  } else if (filtroAtivo === 'hoje') {
    contasFiltradas = contasFonte.filter(c => {
      const v = getVencimento(c);
      if (!v) return false;
      const hoje = new Date();
      hoje.setHours(0,0,0,0);
      const fimDia = new Date(hoje);
      fimDia.setHours(23,59,59,999);
      return v >= hoje && v <= fimDia;
    });
  }

  const filtroArquivosAtual = getFiltroArquivosAtual();
  if (filtroArquivosAtual === 'com_arquivos') {
    contasFiltradas = contasFiltradas.filter(c => temArquivos(c));
  } else if (filtroArquivosAtual === 'sem_arquivos') {
    contasFiltradas = contasFiltradas.filter(c => !temArquivos(c));
  }
  if (filtroTipoDocumento !== 'todos' && filtroArquivosAtual !== 'sem_arquivos') {
    contasFiltradas = contasFiltradas.filter(c => {
      if (!temArquivos(c)) return false;
      const arquivos = Array.isArray(c.arquivos) ? c.arquivos : [];
      const temTipo = arquivos.some(a => detectarTipoDocumento(a) === filtroTipoDocumento);
      return negacaoFiltro ? !temTipo : temTipo;
    });
  }
  if (buscaDocumento && filtroArquivosAtual !== 'sem_arquivos') {
    contasFiltradas = contasFiltradas.filter(c => {
      if (!temArquivos(c)) return false;
      const arquivos = Array.isArray(c.arquivos) ? c.arquivos : [];
      return arquivos.some(a => (a.descricao || a.nome || '').toLowerCase().includes(buscaDocumento.toLowerCase()));
    });
  }
  if (busca) {
    contasFiltradas = contasFiltradas.filter(c => contaCasaComBusca(c, busca));
  }
  // Filtro por categoria/subcategoria (planejamento/conta_analítica)
  if (filtroPlanejamento) {
    contasFiltradas = contasFiltradas.filter(c => normalizeText(c.planejamento || 'Sem planejamento') === normalizeText(filtroPlanejamento));
  }
  if (filtroSubcategoria) {
    contasFiltradas = contasFiltradas.filter(c => normalizeText(c.conta_analitica || c.subcategoria || 'Sem subcategoria') === normalizeText(filtroSubcategoria));
  }
  // Filtro por tipo de pagamento
  if (filtroTipoPagamento !== 'todos') {
    contasFiltradas = contasFiltradas.filter(c => {
      const tipoPagamento = normalizeText(c.tipo_pagamento || '');
      const filtroNormalizado = normalizeText(filtroTipoPagamento);
      return tipoPagamento === filtroNormalizado;
    });
  }
  // Filtro por conta bancária
  if (filtroContaBancaria) {
    contasFiltradas = contasFiltradas.filter(c => {
      const conta = c.conta || 'Não informado';
      return conta === filtroContaBancaria;
    });
  }
  // Filtro por unidade (quando disponível)
  if (filtroUnidade) {
    contasFiltradas = contasFiltradas.filter(c => (c.unidade || null) === filtroUnidade);
  }

  const totalVencidas = contasPagar
    .filter(c => { const v = getVencimento(c); if (!v) return false; const hoje = new Date(); hoje.setHours(0,0,0,0); return v < hoje; })
    .reduce((t, c) => t + (parseFloat(c.valor) || 0), 0);
  const totalProntasParaPagar = contasProntasParaPagar.reduce((t, c) => t + (parseFloat(c.valor) || 0), 0);
  const totalPagas = contasPagas.reduce((t, c) => t + (parseFloat(c.valor) || 0), 0);
  // Mantém a lógica do backup: soma do dataset "próximos 3 meses"
  const totalProximosMeses = contasProximos3Meses.reduce((t, c) => t + (parseFloat(c.valor) || 0), 0);
  const totalRegistrosProntasParaPagar = contasProntasParaPagar.length;
  const totalRegistrosVenceHoje = contasPagar.filter((c) => {
    const v = getVencimento(c);
    if (!v) return false;
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const fimDia = new Date(hoje);
    fimDia.setHours(23, 59, 59, 999);
    return v >= hoje && v <= fimDia;
  }).length;
  const filtroLabels = {
    todas: 'Todas',
    vencidas: 'Vencidas',
    hoje: 'Vencem Hoje',
    proxima_vencimento: 'Próximas 7 dias',
    prontas_para_pagar: 'Prontas para pagar',
    pagas: 'Pagas'
  };
  const filtroAtivoLabel = filtroLabels[filtroAtivo] || 'Todas';
  const ctaLabel = term?.newButtonLabel === 'Novo Título' ? 'Nova Conta' : term?.newButtonLabel || 'Nova Conta';
  const premiumHeaderTokens = {
    '--cp-surface': 'var(--surface)',
    '--cp-surface-soft': 'var(--surface-muted)',
    '--cp-surface-strong': 'var(--surface-strong)',
    '--cp-border': 'var(--border)',
    '--cp-border-strong': 'color-mix(in srgb, var(--border) 42%, var(--text-primary) 58%)',
    '--cp-shadow-soft': '0 8px 20px color-mix(in srgb, var(--text-primary) 12%, transparent)',
    '--cp-shadow-glow': '0 0 0 1px color-mix(in srgb, var(--accent) 22%, transparent)',
    '--cp-radius-card': '0.9rem',
    '--cp-radius-pill': '999px',
    '--cp-motion-fast': '180ms',
    '--cp-motion-base': '220ms'
  };

  return (
    <div className="space-y-4 relative min-h-[400px] w-full max-w-full overflow-x-hidden">
      {notification && (
        <NotificationToast type={notification.type} message={notification.message} onClose={() => setNotification(null)} />
      )}

      {/* Loading Overlay - mantém o layout e bloqueia interação */}
      {loading && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-[color:var(--bg)]/65 backdrop-blur-[1px]">
          <LoadingSpinner
            fullScreen={false}
            className="min-h-[160px] w-full max-w-md"
            cardClassName="w-full"
            label="Carregando contas a pagar"
            subtitle="Aguarde enquanto atualizamos os dados."
          />
        </div>
      )}

      <section
        style={premiumHeaderTokens}
        className="overflow-visible rounded-[var(--cp-radius-card)] border border-[color:var(--cp-border)] bg-[color:var(--cp-surface)] [box-shadow:var(--cp-shadow-soft)]"
      >
        <div className="grid gap-3 border-b border-[color:var(--cp-border)] px-4 py-4 sm:px-5 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
          <div className="space-y-1">
            <p className="text-[11px] uppercase tracking-[0.24em] text-[color:var(--text-muted)]/90">Administrativo</p>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-xl font-semibold tracking-tight text-[color:var(--text-primary)] sm:text-2xl">Contas a Pagar</h2>
              <span className="inline-flex items-center rounded-[var(--cp-radius-pill)] border border-[color:var(--cp-border-strong)] bg-[color:var(--cp-surface-soft)] px-2.5 py-1 text-[11px] font-medium text-[color:var(--text-muted)]">
                Visão: {filtroAtivoLabel}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canCreateContaPagar && (
              <button
                onClick={abrirModalCriar}
                className="hidden sm:inline-flex items-center rounded-[0.7rem] border border-transparent bg-[color:var(--accent)] px-4 py-2 text-sm font-semibold text-white transition-all duration-[var(--cp-motion-base)] hover:-translate-y-0.5 hover:opacity-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--cp-surface)] motion-reduce:transform-none motion-reduce:transition-none"
              >
                {ctaLabel}
              </button>
            )}
          </div>
        </div>

        <div className="border-b border-[color:var(--cp-border)] px-4 py-4 sm:px-5">
          <HeaderStats 
            totalVencidas={totalVencidas} 
            totalProntasParaPagar={totalProntasParaPagar}
            totalProximosMeses={totalProximosMeses}
            totalPagas={totalPagas}
            totalRegistrosProntasParaPagar={totalRegistrosProntasParaPagar}
            totalRegistrosVenceHoje={totalRegistrosVenceHoje}
            filtroAtivo={filtroAtivo}
            loading={loading}
            onChangeFiltro={setFiltroAtivo}
          />
        </div>

        <div className="px-4 py-4 sm:px-5">
          <FiltrosOtimizados
            range={range}
            setRange={setRange}
            loading={loading}
            busca={busca}
            setBusca={setBusca}
            contasFonte={contasFonte}
            filtroPlanejamento={filtroPlanejamento}
            setFiltroPlanejamento={setFiltroPlanejamento}
            filtroSubcategoria={filtroSubcategoria}
            setFiltroSubcategoria={setFiltroSubcategoria}
            filtroTipoPagamento={filtroTipoPagamento}
            setFiltroTipoPagamento={setFiltroTipoPagamento}
            filtroContaBancaria={filtroContaBancaria}
            setFiltroContaBancaria={setFiltroContaBancaria}
            filtroUnidade={filtroUnidade}
            setFiltroUnidade={setFiltroUnidade}
            filtroArquivosAtual={getFiltroArquivosAtual()}
            setFiltroArquivosAtual={setFiltroArquivosAtual}
            filtroTipoDocumento={filtroTipoDocumento}
            setFiltroTipoDocumento={setFiltroTipoDocumento}
            buscaDocumento={buscaDocumento}
            setBuscaDocumento={setBuscaDocumento}
            negacaoFiltro={negacaoFiltro}
            setNegacaoFiltro={setNegacaoFiltro}
            onRefresh={() => {
              setRefreshKey(prev => prev + 1);
              if (filtroAtivo === 'pagas') {
                carregarPagas(true);
              } else if (filtroAtivo === 'prontas_para_pagar') {
                carregarProntasParaPagar(true);
              } else {
                carregarEmAberto(true);
              }
            }}
            onClearAll={() => {
              setFiltroAtivo('todas');
            }}
            contasFiltradas={contasFiltradas}
            totalContas={contasPagar.length}
            filtroAtivo={filtroAtivo}
            setFiltroAtivo={setFiltroAtivo}
            contasPagas={contasPagas}
            contasPagar={contasPagar}
            contasProntasParaPagar={contasProntasParaPagar}
          />
        </div>
      </section>

      {/* Lista de Lotes - Mostrar apenas na aba "Prontas para Pagar" */}
      {filtroAtivo === 'prontas_para_pagar' && (
        <div className="mb-6">
          <ListaLotes
            onLoteClick={handleLoteClick}
            showNotification={showNotification}
            refreshKey={refreshKey}
            onAtivarModoSelecao={handleAtivarModoSelecao}
            onDesativarModoSelecao={handleDesativarModoSelecao}
            onAbrirModalCriarLote={handleAbrirModalCriarLote}
            modoSelecao={modoSelecao}
            canAuthorizePayment={canAuthorizePayment}
            contasSelecionadas={contasSelecionadas}
          />
        </div>
      )}

	      <TabelaContasPagar
	        contasFiltradas={contasFiltradas}
	        hasUnidade={hasUnidade}
	        loading={loading}
	        abrirModal={abrirModal}
	        temArquivos={temArquivos}
	        contarArquivos={contarArquivos}
	        filtroAtivo={filtroAtivo}
        showNotification={showNotification}
        refreshKey={refreshKey}
        modoSelecao={modoSelecao}
        contasSelecionadas={contasSelecionadas}
        onToggleSelecao={handleToggleSelecao}
        contasEmLotes={contasEmLotes}
        onResetVisao={() => {
          setFiltroAtivo('todas');
        }}
        onRefreshListagem={async () => {
          setRefreshKey(prev => prev + 1);
          if (filtroAtivo === 'pagas') {
            await carregarPagas(true);
          } else if (filtroAtivo === 'prontas_para_pagar') {
            await carregarProntasParaPagar(true);
          } else {
            await carregarEmAberto(true);
          }
        }}
        onPagar={async () => {
          // Incrementar refreshKey para forçar atualização dos status de autorização
          setRefreshKey(prev => prev + 1);
          // Recarregar dados após pagamento
          if (filtroAtivo === 'pagas') {
            await carregarPagas(true);
          } else if (filtroAtivo === 'prontas_para_pagar') {
            await carregarProntasParaPagar(true);
          } else {
            await carregarEmAberto(true);
          }
        }}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <GraficoContasPorDia dados={processarDadosGraficoDia(contasPagar)} />
        {/* Usa o dataset específico de próximos 3 meses para o gráfico por mês */}
        <GraficoContasPorMes dados={processarDadosGraficoMes(contasProximos3Meses)} />
      </div>

      {/* <div className="mb-4">
        <ResumoPorCategoria contas={contasPagar} />
      </div> */}

      <div className="mb-4">
        <AnaliseContaBancaria contas={contasPagar} />
      </div>

      {filtroAtivo === 'prontas_para_pagar' && modoSelecao && (
        <div className="fixed inset-x-3 bottom-4 z-40 sm:hidden">
          <div className="flex items-center justify-between rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-2 shadow-lg">
            <p className="text-xs font-medium text-[color:var(--text-muted)]">
              {contasSelecionadas.length} selecionada{contasSelecionadas.length === 1 ? '' : 's'}
            </p>
            <button
              type="button"
              onClick={handleAbrirModalCriarLote}
              disabled={contasSelecionadas.length === 0}
              className="inline-flex min-h-[44px] items-center rounded-lg bg-[color:var(--accent)] px-3 py-2 text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40"
            >
              Criar lote
            </button>
          </div>
        </div>
      )}

      {/* Botão flutuante - mobile */}
      {canCreateContaPagar && (
        <button
          onClick={abrirModalCriar}
          className={`sm:hidden fixed bottom-5 right-4 z-40 inline-flex min-h-[44px] items-center gap-2 px-4 py-3 rounded-full shadow-lg bg-[color:var(--accent)] text-white text-sm font-semibold hover:opacity-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] ${
            filtroAtivo === 'prontas_para_pagar' && modoSelecao ? 'hidden' : ''
          }`}
        >
          {ctaLabel}
        </button>
      )}

      {modalAberto && contaSelecionada && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-2 sm:items-center sm:p-4 overflow-hidden"
          onKeyDown={handleDetalhesModalKeyDown}
        >
          <div
            ref={detalhesModalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="detalhes-conta-titulo"
            className="my-0 max-h-[calc(100dvh-1rem)] w-full max-w-4xl overflow-y-auto overscroll-contain rounded-xl bg-[color:var(--surface)] shadow-xl sm:my-auto sm:max-h-[95vh] sm:rounded-lg"
          >
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-[color:var(--border)] sticky top-0 bg-[color:var(--surface)] z-10">
              <h3 id="detalhes-conta-titulo" className="text-base sm:text-lg font-semibold text-[color:var(--text-primary)] pr-2">Detalhes da Conta #{contaSelecionada.id}</h3>
			              <button
                    ref={detalhesModalCloseRef}
			                onClick={fecharModal}
			                className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] transition-colors flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)] rounded"
			                aria-label="Fechar detalhes da conta"
			              >
		                <X className="h-5 w-5 sm:h-6 sm:w-6" />
		              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-5">
              {/* Resumo rápido */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-3">
                  <p className="text-xs text-[color:var(--text-muted)]">Fornecedor</p>
                  <p className="text-sm font-semibold text-[color:var(--text-primary)] mt-1 truncate" title={contaSelecionada.fornecedor}>
                    {contaSelecionada.fornecedor}
                  </p>
                </div>
                <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-3">
                  <p className="text-xs text-[color:var(--text-muted)]">Valor Total</p>
                  <p className="text-sm font-semibold text-[color:var(--text-primary)] mt-1">
                    {formatarMoeda(parseFloat(contaSelecionada.valor) || 0)}
                  </p>
                </div>
                <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-3">
                  <p className="text-xs text-[color:var(--text-muted)]">Vencimento</p>
                  <p className="text-sm font-semibold text-[color:var(--text-primary)] mt-1">
                    {(() => { const v = getVencimento(contaSelecionada); return v ? v.toLocaleDateString('pt-BR') : 'Data inválida'; })()}
                  </p>
                </div>
                <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-3">
                  <p className="text-xs text-[color:var(--text-muted)]">Status</p>
                  {(() => {
                    const hoje = new Date();
                    const vencimento = getVencimento(contaSelecionada);
                    if (!vencimento) {
                      return <p className="text-sm font-semibold text-[color:var(--text-primary)] mt-1">N/A</p>;
                    }
                    hoje.setHours(0, 0, 0, 0);
                    vencimento.setHours(0, 0, 0, 0);
                    const diasVencimento = Math.floor((vencimento - hoje) / (1000 * 60 * 60 * 24));
                    const statusInfo = getContaStatusInfo(contaSelecionada.status, diasVencimento);
                    return (
                      <span className={`mt-1 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusInfo.bgColor} ${statusInfo.color}`}>
                        {statusInfo.icon}
                        <span>{statusInfo.label}</span>
                      </span>
                    );
                  })()}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {/* Informações Básicas */}
                <div className="space-y-4">
                  <h4 className="font-medium text-[color:var(--text-primary)] border-b pb-2">Informações Básicas</h4>
                  
                  <div>
                    <label className="text-sm font-medium text-[color:var(--text-muted)]">Descrição</label>
                    <p className="text-sm text-[color:var(--text-primary)] mt-1">{contaSelecionada.observacao || contaSelecionada.descricao}</p>
                  </div>
                  
	                  <div>
	                    <label className="text-sm font-medium text-[color:var(--text-muted)]">Classificação</label>
	                    <p className="text-sm text-[color:var(--text-primary)] mt-1">
	                      {contaSelecionada.planejamento || 'Sem planejamento'} › {contaSelecionada.conta_analitica || 'Sem subcategoria'}
	                    </p>
	                  </div>
	                  
	                  {contaSelecionada.unidade && (
	                    <div>
	                      <label className="text-sm font-medium text-[color:var(--text-muted)]">Unidade</label>
	                      <p className="text-sm text-[color:var(--text-primary)] mt-1">{contaSelecionada.unidade}</p>
	                    </div>
	                  )}

	                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
	                    <div>
	                      <label className="text-sm font-medium text-[color:var(--text-muted)]">Documento</label>
	                      <p className="text-sm text-[color:var(--text-primary)] mt-1">{contaSelecionada.documento || 'Não informado'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[color:var(--text-muted)]">Tipo de Pagamento</label>
                      <p className="text-sm text-[color:var(--text-primary)] mt-1">{contaSelecionada.tipo_pagamento || 'Não informado'}</p>
                    </div>
                  </div>
                  
                  {/* Código de Barras - apenas para Boleto */}
                  {contaSelecionada.tipo_pagamento?.toLowerCase()?.trim() === 'boleto' && contaSelecionada.codigo_barras && (
                    <div>
                      <label className="text-sm font-medium text-[color:var(--text-muted)]">Código de Barras</label>
                      <div className="mt-1 flex items-start gap-2">
                        <p className="text-sm text-[color:var(--text-primary)] font-mono break-all">{contaSelecionada.codigo_barras}</p>
	                        <button
	                          type="button"
	                          onClick={() => copiarTexto(contaSelecionada.codigo_barras, 'Código de barras')}
	                          className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md border border-[color:var(--border)] bg-[color:var(--surface-muted)] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
	                          aria-label="Copiar código de barras"
	                          title="Copiar código de barras"
	                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <label className="text-sm font-medium text-[color:var(--text-muted)]">Conta Bancária</label>
                    <p className="text-sm text-[color:var(--text-primary)] mt-1">{contaSelecionada.conta || 'Não informado'}</p>
                  </div>
                </div>

                {/* Informações Financeiras */}
                <div className="space-y-4">
                  <h4 className="font-medium text-[color:var(--text-primary)] border-b pb-2">Informações Financeiras</h4>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-3">
                      <label className="text-xs font-medium text-[color:var(--text-muted)]">Em Aberto</label>
	                      <p className="text-sm font-semibold text-[color:var(--status-danger-fg,var(--text-primary))] mt-1">
	                        {formatarMoeda(parseFloat(contaSelecionada.valor_aberto) || 0)}
	                      </p>
                    </div>
                    <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-3">
                      <label className="text-xs font-medium text-[color:var(--text-muted)]">Pago</label>
	                      <p className="text-sm font-semibold text-[color:var(--status-good-fg,var(--text-primary))] mt-1">
	                        {formatarMoeda(parseFloat(contaSelecionada.valor_pago) || 0)}
	                      </p>
                    </div>
                    <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-3">
                      <label className="text-xs font-medium text-[color:var(--text-muted)]">Valor Total</label>
                      <p className="text-sm font-semibold text-[color:var(--text-primary)] mt-1">
                        {formatarMoeda(parseFloat(contaSelecionada.valor) || 0)}
                      </p>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-[color:var(--text-muted)]">Data de Vencimento</label>
                    <p className="text-sm text-[color:var(--text-primary)] mt-1">{(() => { const v = getVencimento(contaSelecionada); return v ? v.toLocaleDateString('pt-BR') : 'Data inválida'; })()}</p>
                  </div>
                  
                  {(() => {
                    const dataPagamentoStr = contaSelecionada.data_pagamento || contaSelecionada.dataPagamento;
                    if (!dataPagamentoStr) return null;
                    
                    return (
                      <div>
                        <label className="text-sm font-medium text-[color:var(--text-muted)]">Data de Pagamento</label>
                        <p className="text-sm text-[color:var(--text-primary)] mt-1">
                          {(() => {
                            const dataPagamento = getDataPagamento({ data_pagamento: dataPagamentoStr });
                            if (dataPagamento) {
                              const dataFormatada = dataPagamento.toLocaleDateString('pt-BR');
                              const hora = dataPagamento.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                              return hora !== '00:00' ? `${dataFormatada} ${hora}` : dataFormatada;
                            }
                            // Fallback: exibir a string original se não conseguir parsear
                            return dataPagamentoStr;
                          })()}
                        </p>
                      </div>
                    );
                  })()}
                  
                  <div>
                    <label className="text-sm font-medium text-[color:var(--text-muted)]">Dias para Vencimento</label>
                    <p className={`text-sm font-medium mt-1 ${
	                      contaSelecionada.diasVencimento < 0 ? 'text-[color:var(--status-danger-fg,var(--text-primary))]' : 
	                      contaSelecionada.diasVencimento <= 7 ? 'text-[color:var(--status-warning-fg,var(--text-primary))]' : 'text-[color:var(--status-good-fg,var(--text-primary))]'
                    }`}>
                      {contaSelecionada.status === 'paga' || contaSelecionada.status === 'cancelada' || contaSelecionada.status === 'paga_parcialmente' ? 
                        'Conta processada' :
                        contaSelecionada.diasVencimento < 0 ? 
                        `Vencida há ${Math.abs(contaSelecionada.diasVencimento)} dias` :
                        contaSelecionada.diasVencimento === 0 ? 'Vence hoje' :
                        `Vence em ${contaSelecionada.diasVencimento} dias`
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* Arquivos */}
              <div className="mt-6 w-full max-w-full overflow-x-hidden">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-4">
                  <h4 className="font-medium text-[color:var(--text-primary)] border-b pb-2 flex-1">Arquivos Anexados</h4>
	                  {canUploadContaPagarAttachment && arquivosNovos.length > 0 && (
	                    <button
	                      onClick={enviarArquivos}
	                      disabled={uploadingArquivos}
	                      className="w-full sm:w-auto min-h-[44px] px-3 py-2 text-sm font-medium text-white bg-[color:var(--accent)] rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
	                    >
	                      {uploadingArquivos ? 'Enviando...' : `Enviar ${arquivosNovos.length} arquivo(s)`}
	                    </button>
                  )}
                </div>
                
                {/* Lista de arquivos existentes */}
                {temArquivos(contaSelecionada) && (
                  <div className="space-y-3 mb-4 w-full max-w-full overflow-x-hidden">
                    {Array.isArray(contaSelecionada.arquivos) && contaSelecionada.arquivos.map((arquivo, index) => (
                      <ArquivoItem key={arquivo.id || index} arquivo={arquivo} contaId={contaSelecionada.id} />
                    ))}
                  </div>
                )}
                
                {/* Área para adicionar novos arquivos */}
                {canUploadContaPagarAttachment && (
                  <div className="border-t pt-4">
                    <label className="block text-sm font-medium text-[color:var(--text-primary)] mb-2">Adicionar Novo Arquivo</label>
                    <div className="relative">
                      <div className="border-2 border-dashed border-[color:var(--border)] rounded-lg p-4 text-center hover:border-[color:var(--accent)] hover:bg-[color:var(--surface-muted)] transition-all duration-200">
                        <input
                          type="file"
                          accept="application/pdf,image/*"
                          multiple
                          onChange={handleAdicionarArquivos}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                          disabled={uploadingArquivos}
                        />
                        <div className="pointer-events-none">
                          <Upload className="h-5 w-5 mx-auto text-[color:var(--text-muted)] mb-2" />
                          <p className="text-sm text-[color:var(--text-muted)] mb-1">
                            Clique para selecionar arquivos
                          </p>
                          <p className="text-xs text-[color:var(--text-muted)]">
                            PDF, PNG, JPG, JPEG, GIF, WEBP | Máx: 8 MB
                          </p>
                        </div>
                      </div>
                    </div>
                  
                    {/* Lista de arquivos selecionados para upload */}
                    {arquivosNovos.length > 0 && (
                      <div className="mt-3 space-y-3">
                        {arquivosNovos.map((arquivo, idx) => {
                        const getFileIcon = () => {
                          const fileName = arquivo.file.name.toLowerCase();
	                          if (fileName.endsWith('.pdf')) return <FileText className="h-4 w-4 text-[color:var(--status-danger-fg,var(--text-primary))]" />;
	                          if (['.png', '.jpg', '.jpeg', '.gif', '.webp'].some(ext => fileName.endsWith(ext))) {
	                            return <ImageIcon className="h-4 w-4 text-[color:var(--status-info-fg,var(--text-primary))]" />;
	                          }
                          return <File className="h-4 w-4 text-[color:var(--text-muted)]" />;
                        };
                        
                        return (
                          <div key={idx} className="border rounded-lg p-3 bg-[color:var(--surface-muted)]">
                            <div className="flex items-start gap-3 mb-2">
                              <div className="flex-shrink-0 mt-0.5">
                                {getFileIcon()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-[color:var(--text-primary)] truncate" title={arquivo.file.name}>
                                  {arquivo.file.name}
                                </p>
                                <p className="text-xs text-[color:var(--text-muted)] mt-0.5">
                                  {((arquivo.file.size || 0) / 1024 / 1024).toFixed(2)} MB
                                </p>
                              </div>
	                              <button 
	                                onClick={() => removerArquivoNovo(idx)}
	                                className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center flex-shrink-0 text-[color:var(--status-danger-fg,var(--text-primary))] hover:opacity-80 p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
	                                disabled={uploadingArquivos}
	                                title="Remover arquivo"
                                  aria-label={`Remover arquivo ${arquivo.file.name}`}
	                              >
                                <XCircle className="h-4 w-4" />
                              </button>
                            </div>
                            <div className="mt-2">
                              <label className="block text-xs font-medium text-[color:var(--text-primary)] mb-1">
                                Descrição
                              </label>
                              <input
                                type="text"
                                value={arquivo.descricao || ''}
                                onChange={(e) => setArquivosNovos(prev => 
                                  prev.map((x, i) => i === idx ? { ...x, descricao: e.target.value } : x)
                                )}
                                placeholder="Adicione uma descrição para o arquivo"
                                disabled={uploadingArquivos}
                                className="w-full min-h-[44px] px-3 py-2 text-sm border border-[color:var(--border)] rounded-lg focus:ring-2 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)] disabled:bg-[color:var(--surface-muted)] disabled:cursor-not-allowed"
                              />
                            </div>
                          </div>
                        );
                      })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Seção de Erro - se houver */}
              {authorization?.error_message && (
	                <div className="mt-6 p-4 bg-[color:var(--status-danger-bg,var(--surface-muted))] border border-[color:var(--status-danger-border,var(--border))] rounded-lg">
	                  <div className="flex items-start gap-3">
	                    <AlertTriangle className="h-5 w-5 text-[color:var(--status-danger-fg,var(--text-primary))] flex-shrink-0 mt-0.5" />
	                    <div className="flex-1">
	                      <h4 className="text-sm font-semibold text-[color:var(--status-danger-fg,var(--text-primary))] mb-2">
	                        Erro no Processamento do Pagamento
	                      </h4>
	                      <p className="text-sm text-[color:var(--status-danger-fg,var(--text-primary))] mb-2">
	                        {authorization.error_message}
	                      </p>
                      {authorization.failed_at && (
	                        <p className="text-xs text-[color:var(--text-muted)] mb-3">
                          Ocorreu em: {new Date(authorization.failed_at).toLocaleString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      )}
                      {authorization.payment_result && (
                        <details className="mt-2">
	                          <summary className="text-xs text-[color:var(--status-danger-fg,var(--text-primary))] cursor-pointer hover:opacity-80 font-medium">
	                            Ver detalhes técnicos
	                          </summary>
	                          <div className="mt-2 text-xs bg-[color:var(--surface)] p-2 rounded border border-[color:var(--status-danger-border,var(--border))]">
                            <pre className="whitespace-pre-wrap break-words">
                              {JSON.stringify(authorization.payment_result, null, 2)}
                            </pre>
                          </div>
                        </details>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Ações */}
              <div className="mt-6 flex flex-col sm:flex-row justify-end gap-3">
	                <button
	                  onClick={fecharModal}
	                  className="w-full sm:w-auto min-h-[44px] px-4 py-2 text-sm font-medium text-[color:var(--text-primary)] bg-[color:var(--surface-muted)] border border-[color:var(--border)] rounded-md hover:bg-[color:var(--surface)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
	                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ModalCriarContaPagar
        isOpen={modalCriarAberto}
        onClose={fecharModalCriar}
        canUploadAttachments={canUploadContaPagarAttachment}
        onCreated={async () => {
          showNotification('Conta a pagar criada com sucesso');
          // Aguardar um pouco para garantir que o N8N processou
          await new Promise(resolve => setTimeout(resolve, 500));
          // Recarregar dados FORÇANDO bypass do cache
          if (filtroAtivo === 'pagas') {
            await carregarPagas(true);
          } else if (filtroAtivo === 'prontas_para_pagar') {
            await carregarProntasParaPagar(true);
          } else {
            await carregarEmAberto(true);
          }
          
          // Se o modal de visualização estiver aberto, atualizar os dados
          if (modalAberto && contaSelecionada) {
            setTimeout(() => {
              atualizarContaSelecionada();
            }, 300);
          }
        }}
      />

      {/* Modal de criação de lote */}
      <ModalCriarLote
        isOpen={modalCriarLoteAberto}
        onClose={() => setModalCriarLoteAberto(false)}
        contasSelecionadas={contasFiltradas.filter(conta => contasSelecionadas.includes(conta.id))}
        onLoteCriado={handleLoteCriado}
      />

      {/* Modal de detalhes do lote */}
      {loteSelecionado && (
        <DetalhesLote
          loteId={loteSelecionado}
          onClose={handleFecharDetalhesLote}
          showNotification={showNotification}
          contasProntasParaPagar={contasProntasParaPagar}
        />
      )}
    </div>
  );
};

export default ContasPagar;
